/**
 * Shortcut registry — the single source of truth for the shortcut cheatsheet
 * (the `?` dialog). Static app-wide shortcuts live in `SHORTCUTS` below;
 * context-scoped shortcuts (Ctrl+S on a form page, Ctrl+P on a detail page)
 * mount themselves at render time through `registerShortcut` / `useRegistered
 * Shortcut`, and are unmounted with the same route.
 *
 * The registry itself doesn't fire shortcuts — that's `useHotkey`'s job. The
 * registry only owns what's rendered on the `?` cheatsheet.
 */
import { useEffect, useSyncExternalStore } from 'react';

/* -------------------------------------------------------------------------- */
/*  Public types                                                              */
/* -------------------------------------------------------------------------- */

export interface ShortcutSpec {
  /** Key spec as accepted by `useHotkey` — e.g. 'ctrl+s', 'g d', '?'. */
  keys: string;
  /** Cheatsheet label — a short verb phrase. */
  label: string;
  /** Cheatsheet section — "Global", "List page", "Form", "Detail page". */
  group: ShortcutGroup;
  /** Optional longer description for the cheatsheet row. */
  description?: string;
}

export type ShortcutGroup = 'Global' | 'Navigation' | 'List page' | 'Form' | 'Detail page';

/* -------------------------------------------------------------------------- */
/*  Static app-wide shortcuts                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Static registrations — mounted on the cheatsheet even when no page owning
 * them is currently open (they're operator "you can always do this" muscle
 * memory). Runtime shortcuts (from ListPage, DetailPage, FormShortcuts) are
 * appended via `registerShortcut` on mount so the cheatsheet reflects the
 * current page's capabilities.
 */
export const SHORTCUTS: ShortcutSpec[] = [
  { keys: 'ctrl+k', label: 'Open command palette', group: 'Global' },
  { keys: '/',      label: 'Focus global search',  group: 'Global' },
  { keys: '?',      label: 'Show this cheatsheet', group: 'Global' },

  { keys: 'g d', label: 'Go to Dashboard',      group: 'Navigation' },
  { keys: 'g i', label: 'Go to Sales Invoices', group: 'Navigation' },
  { keys: 'g o', label: 'Go to Sales Orders',   group: 'Navigation' },
  { keys: 'g q', label: 'Go to Quotations',     group: 'Navigation' },
  { keys: 'g p', label: 'Go to Products',       group: 'Navigation' },
  { keys: 'g c', label: 'Go to Customers',      group: 'Navigation' },
  { keys: 'g s', label: 'Go to Suppliers',      group: 'Navigation' },
  { keys: 'g m', label: 'Go to Manufacturing Board', group: 'Navigation' },
  { keys: 'g h', label: 'Go to Help',           group: 'Navigation' },

  { keys: 'ctrl+n', label: 'Create new item',   group: 'List page' },
  { keys: 'f',      label: 'Focus table search', group: 'List page' },
  { keys: 'e',      label: 'Export table',      group: 'List page' },
  { keys: 'i',      label: 'Import',            group: 'List page' },
  { keys: 'delete', label: 'Delete selected rows', group: 'List page' },

  { keys: 'ctrl+s',     label: 'Save form',    group: 'Form' },
  { keys: 'ctrl+enter', label: 'Save form',    group: 'Form' },

  { keys: 'ctrl+p', label: 'Print / open PDF', group: 'Detail page' },
  { keys: 'e',      label: 'Edit document',    group: 'Detail page' },
  { keys: 'delete', label: 'Delete document',  group: 'Detail page' },
];

/* -------------------------------------------------------------------------- */
/*  Dynamic registry — subscribe/store pattern                                */
/* -------------------------------------------------------------------------- */

const dynamic = new Set<ShortcutSpec>();
const listeners = new Set<() => void>();
let cachedSnapshot: ShortcutSpec[] | null = null;

function notify() {
  cachedSnapshot = null;
  for (const l of listeners) l();
}

/** Add a shortcut to the cheatsheet listing. Returns the unregister fn. */
export function registerShortcut(spec: ShortcutSpec): () => void {
  dynamic.add(spec);
  notify();
  return () => {
    dynamic.delete(spec);
    notify();
  };
}

/** Direct removal — matches an entry by reference. */
export function unregisterShortcut(spec: ShortcutSpec): void {
  if (dynamic.delete(spec)) notify();
}

/**
 * Register a shortcut for the lifetime of the calling component (mount →
 * unmount). Pass `when=false` to conditionally suppress.
 */
export function useRegisteredShortcut(spec: ShortcutSpec, when: boolean = true): void {
  const { keys, label, group, description } = spec;
  useEffect(() => {
    if (!when) return;
    return registerShortcut({ keys, label, group, description });
  }, [keys, label, group, description, when]);
}

/* -------------------------------------------------------------------------- */
/*  Snapshot                                                                  */
/* -------------------------------------------------------------------------- */

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

function getSnapshot(): ShortcutSpec[] {
  if (!cachedSnapshot) {
    cachedSnapshot = [...SHORTCUTS, ...dynamic];
  }
  return cachedSnapshot;
}

/** Read the current set of shortcuts (static + dynamic) reactively. */
export function useAllShortcuts(): ShortcutSpec[] {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
